import { Suspense, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { DetailLoading } from "~/components/loading";
import { useGroupActivation, useGroupList } from "~/features/admin/groups/hooks";
import { toGroupDetailUI } from "~/features/admin/groups/utils/transformers";
import { GroupDetailPresenter } from "./GroupDetailPresenter";
import { GroupFormDialog } from "./GroupFormDialog";
import { GroupUsers } from "./GroupUsers";

interface GroupDetailProps {
  groupId: string;
  /** Back action: close the sheet (peek) or navigate to the list (full page). */
  onBack: () => void;
  /** Render the "users in group" section. V1 showed it in the Peek only. */
  showUsers?: boolean;
}

/**
 * Reusable from both the full-page route and the Peek side panel. Owns data,
 * the edit dialog, and the activation confirm flow; renders the presenter.
 * Reads the group from the shared list query and finds it by id (V1 parity).
 */
export function GroupDetail({ groupId, onBack, showUsers = false }: GroupDetailProps) {
  const { t } = useTranslation();
  const { data } = useGroupList();
  const { onActivate, onDeactivate, processing } = useGroupActivation();
  const [pendingAction, setPendingAction] = useState<"activate" | "deactivate" | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const raw = data.userGroups.find((g) => g.id === groupId);
  const detail = useMemo(() => (raw ? toGroupDetailUI(raw) : null), [raw]);

  if (!detail) {
    return (
      <div className="rounded-[var(--radius-md)] border border-border-default bg-white px-5 py-12 text-center text-14-regular text-fg-2">
        {t("ui.notFound")}
      </div>
    );
  }

  const handleConfirm = async () => {
    if (pendingAction === "activate") {
      await onActivate({ id: detail.id });
    } else if (pendingAction === "deactivate") {
      await onDeactivate({ id: detail.id });
    }
    setPendingAction(null);
  };

  return (
    <>
      <GroupDetailPresenter
        group={detail}
        onBack={onBack}
        onEdit={() => setEditOpen(true)}
        onActivate={() => setPendingAction("activate")}
        onDeactivate={() => setPendingAction("deactivate")}
        pendingAction={pendingAction}
        processing={processing}
        onConfirm={handleConfirm}
        onCancelConfirm={() => setPendingAction(null)}
        usersSection={
          showUsers ? (
            <Suspense fallback={<DetailLoading />}>
              <GroupUsers groupId={detail.id} />
            </Suspense>
          ) : undefined
        }
      />
      <GroupFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        group={{ id: detail.id, name: detail.name, description: detail.description }}
      />
    </>
  );
}
